// frontend/packages/business-console/src/pages/QuoteDetail.jsx
//
// Work item 2.15 — Quote detail page (`/business/:slug/quotes/:id`).
// Spec source: `docs/design/quotes-invoices-console.md` §3.4.
//
// Layout:
//   - Top: breadcrumb back-link + state chip + state-machine-aware
//     action bar.
//   - Convert-to-invoice success banner above the body when state ==
//     `accepted` (§3.9). Clicking calls the existing 2.8 convert
//     endpoint and redirects to the new invoice's edit route (which
//     today renders the editor placeholder).
//   - Two-column body on desktop: WYSIWYG document view (max 720) +
//     right rail with the activity timeline. Stacks vertically on
//     mobile.
//   - Confirmation dialogs gate every state-transition action (send,
//     decline, delete) — these are irreversible per the design doc
//     §3.7.

import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { getApiBase } from '@upryzze/services';
import { BusinessContext } from '../contexts/BusinessContext';
import { useQuoteDetail } from './Quotes/useQuoteDetail';
import QuoteDocumentView from './Quotes/QuoteDocumentView';
import QuoteActivityTimeline from './Quotes/QuoteActivityTimeline';
import {
  acceptQuote,
  convertQuoteToInvoice,
  declineQuote,
  deleteQuoteDraft,
  sendQuote,
  resendQuote,
} from '../services/quoteService';

const STATE_COLOR = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  accepted: 'success',
  declined: 'error',
  expired: 'default',
  converted: 'success',
  void: 'default',
};

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function buildPublicLink(viewToken) {
  if (!viewToken) return null;
  if (typeof window === 'undefined') return `/q/${viewToken}`;
  return `${window.location.origin}/q/${viewToken}`;
}

// The PDF lives on the BACKEND host at /q/:token/pdf, which 302-redirects
// to a freshly SAS-signed blob URL. Linking to quote.pdf.blobUrl directly
// hits the private blob with no token -> Azure ResourceNotFound (404).
// Build from the API base origin so it works regardless of Console host.
function buildPdfDownloadUrl(viewToken) {
  if (!viewToken) return null;
  try {
    const origin = new URL(getApiBase(), window.location.origin).origin;
    return `${origin}/q/${viewToken}/pdf`;
  } catch {
    return `/q/${viewToken}/pdf`;
  }
}

export default function QuoteDetail() {
  const { business } = useContext(BusinessContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const {
    quote,
    customer,
    availableActions,
    business: demoBusiness,
    loading,
    error,
    refresh,
    isDemo,
  } = useQuoteDetail(id);

  // Action plumbing
  const [pendingAction, setPendingAction] = useState(null); // 'send' | 'accept' | 'decline' | 'delete' | 'convert' | null
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [toast, setToast] = useState(null);

  // Operator "mark as accepted" form state. `name` prefills from the customer
  // on file (editable); `note` is required (how it was accepted).
  const [acceptName, setAcceptName] = useState('');
  const [acceptNote, setAcceptNote] = useState('');

  const openAcceptDialog = () => {
    setAcceptName(customer ? customerName(customer) : '');
    setAcceptNote('');
    setActionError(null);
    setPendingAction('accept');
  };

  const closeDialog = () => setPendingAction(null);

  const acceptReady =
    acceptName.trim().length > 0 && acceptNote.trim().length > 0;

  const handleConfirm = async () => {
    if (!pendingAction || !quote) return;
    setActing(true);
    setActionError(null);
    try {
      if (pendingAction === 'send') {
        await sendQuote(quote._id);
        setToast({
          severity: 'success',
          text: `${quote.number} sent.`,
        });
      } else if (pendingAction === 'resend') {
        await resendQuote(quote._id);
        setToast({
          severity: 'success',
          text: `${quote.number} resent.`,
        });
      } else if (pendingAction === 'accept') {
        if (!acceptReady) {
          setActing(false);
          return;
        }
        await acceptQuote(quote._id, {
          name: acceptName.trim(),
          note: acceptNote.trim(),
        });
        setToast({
          severity: 'success',
          text: 'Estimate marked accepted — ready to invoice.',
        });
      } else if (pendingAction === 'decline') {
        await declineQuote(quote._id);
        setToast({ severity: 'info', text: 'Estimate marked declined.' });
      } else if (pendingAction === 'delete') {
        await deleteQuoteDraft(quote._id);
        setToast({ severity: 'info', text: 'Draft deleted.' });
        // Navigate back to list — the quote no longer exists.
        navigate(`${basePath}/quotes`);
        return;
      } else if (pendingAction === 'convert') {
        const result = await convertQuoteToInvoice(quote._id);
        const newId = result?.invoice?._id;
        if (newId) {
          navigate(`${basePath}/invoices/${newId}/edit`);
          return;
        }
      }
      closeDialog();
      refresh();
    } catch (err) {
      setActionError(
        err?.response?.data?.error || err?.message || 'Action failed.'
      );
    } finally {
      setActing(false);
    }
  };

  const handleCopyLink = async () => {
    const link = buildPublicLink(quote?.viewToken);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setToast({ severity: 'success', text: 'Public link copied.' });
    } catch {
      setToast({
        severity: 'error',
        text: "Couldn't copy — try selecting the URL manually.",
      });
    }
  };

  // ── Loading / error / not-found shells ─────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/quotes`} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.response?.data?.error ||
            error?.message ||
            "Couldn't load this estimate."}
        </Alert>
      </Box>
    );
  }
  if (!quote) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/quotes`} />
        <Alert severity="warning" sx={{ mt: 2 }}>
          Estimate not found.
        </Alert>
      </Box>
    );
  }

  const a = availableActions || {};
  const isAccepted = quote.state === 'accepted';
  const isConverted = Boolean(quote.convertedInvoiceId);
  const publicLink = buildPublicLink(quote.viewToken);

  // ── Render ────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {/* ── Action bar / breadcrumb ──────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BackLink to={`${basePath}/quotes`} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            /
          </Typography>
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {quote.number}
          </Typography>
          <Chip
            size="small"
            label={quote.state}
            color={STATE_COLOR[quote.state] || 'default'}
            variant="outlined"
            sx={{ textTransform: 'capitalize', fontWeight: 600 }}
          />
          {isDemo ? (
            <Chip
              size="small"
              label="Demo"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', gap: 1, rowGap: 1 }}
        >
          {a.canEdit ? (
            <Button
              size="small"
              startIcon={<EditOutlinedIcon />}
              component={RouterLink}
              to={`${basePath}/quotes/${quote._id}/edit`}
            >
              Edit
            </Button>
          ) : null}
          {a.canSend ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              onClick={() => setPendingAction('send')}
            >
              Send
            </Button>
          ) : null}
          {a.canResend ? (
            <Button
              size="small"
              startIcon={<SendOutlinedIcon />}
              onClick={() => setPendingAction('resend')}
            >
              Resend
            </Button>
          ) : null}
          {publicLink ? (
            <Tooltip title={publicLink}>
              <Button
                size="small"
                startIcon={<LinkRoundedIcon />}
                onClick={handleCopyLink}
              >
                Copy link
              </Button>
            </Tooltip>
          ) : null}
          {a.canAccept ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<TaskAltRoundedIcon />}
              onClick={openAcceptDialog}
            >
              Mark as accepted
            </Button>
          ) : null}
          {a.canConvert ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<SwapHorizRoundedIcon />}
              onClick={() => setPendingAction('convert')}
              disabled={isConverted}
            >
              {isConverted ? 'Converted' : 'Convert to invoice'}
            </Button>
          ) : null}
          {a.canDecline ? (
            <Button
              size="small"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => setPendingAction('decline')}
            >
              Decline
            </Button>
          ) : null}
          {a.canDelete ? (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => setPendingAction('delete')}
            >
              Delete
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* ── Accepted banner (drives the convert-to-invoice CTA) ──── */}
      {isAccepted ? (
        <Alert
          severity="success"
          icon={<CheckCircleRoundedIcon fontSize="small" />}
          action={
            isConverted ? (
              <Button
                size="small"
                color="inherit"
                component={RouterLink}
                to={`${basePath}/invoices/${quote.convertedInvoiceId}`}
              >
                Open invoice
              </Button>
            ) : (
              <Button
                size="small"
                color="inherit"
                onClick={() => setPendingAction('convert')}
              >
                Convert to invoice
              </Button>
            )
          }
        >
          {isConverted ? (
            <>This estimate was accepted and converted to an invoice.</>
          ) : (
            <>
              {quote.acceptedBy?.via === 'operator' ? (
                <>
                  Marked accepted by your team
                  {quote.acceptedAt
                    ? ` on ${new Date(quote.acceptedAt).toLocaleDateString(
                        'en-US',
                        { month: 'short', day: 'numeric' }
                      )}`
                    : ''}
                  {quote.acceptedBy?.note ? ` — ${quote.acceptedBy.note}` : ''}.
                  {'  '}Ready to invoice them?
                </>
              ) : (
                <>
                  This estimate was accepted by{' '}
                  {customer ? customerName(customer) : 'the customer'}
                  {quote.acceptedAt
                    ? ` on ${new Date(quote.acceptedAt).toLocaleDateString(
                        'en-US',
                        { month: 'short', day: 'numeric' }
                      )}`
                    : ''}
                  .{'  '}Ready to invoice them?
                </>
              )}
            </>
          )}
        </Alert>
      ) : null}

      {/* ── Body: document + activity ────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 280px' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <QuoteDocumentView
            quote={quote}
            customer={customer}
            business={demoBusiness || { name: business?.name || '' }}
          />
        </Box>
        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <QuoteActivityTimeline quote={quote} />
          {quote.pdf?.blobUrl && quote.viewToken ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                component="a"
                href={buildPdfDownloadUrl(quote.viewToken)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download sent PDF
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* ── Confirmation dialogs ─────────────────────────────────── */}
      <Dialog open={pendingAction === 'send'} onClose={closeDialog}>
        <DialogTitle>Send {quote.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends the <strong>PDF and accept link</strong> to{' '}
            <strong>
              {customer ? customerName(customer) : 'the customer'}
            </strong>
            {customer?.email ? (
              <>
                {' '}
                (<code>{customer.email}</code>)
              </>
            ) : null}{' '}
            and locks the estimate. You can void it later but you can&apos;t
            unsend.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
          >
            {acting ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'resend'} onClose={closeDialog}>
        <DialogTitle>Resend {quote.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This re-sends the same email (with the PDF and accept link) to{' '}
            <strong>
              {customer ? customerName(customer) : 'the customer'}
            </strong>
            {customer?.email ? (
              <>
                {' '}
                (<code>{customer.email}</code>)
              </>
            ) : null}
            . The estimate stays as is; nothing about it changes.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
          >
            {acting ? 'Sending…' : 'Resend now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingAction === 'accept'}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Mark {quote.number} as accepted?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this when the customer accepted off-link (by phone, in person,
            or email). This is recorded on the estimate&apos;s history, then you
            can convert it to an invoice.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Accepted by"
            value={acceptName}
            onChange={(e) => setAcceptName(e.target.value)}
            disabled={acting}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="How was it accepted?"
            placeholder="e.g. confirmed by phone, 5/31"
            value={acceptNote}
            onChange={(e) => setAcceptNote(e.target.value)}
            disabled={acting}
            required
            multiline
            minRows={2}
            helperText="Required — recorded on the estimate's history."
          />
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting || !acceptReady}
            startIcon={
              acting ? <CircularProgress size={14} /> : <TaskAltRoundedIcon />
            }
          >
            {acting ? 'Working…' : 'Mark accepted'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'decline'} onClose={closeDialog}>
        <DialogTitle>Mark {quote.number} as declined?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Use this when the customer told you (off-link) that they&apos;re not
            moving forward. The estimate will close out and stop counting toward
            your pipeline.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={acting ? <CircularProgress size={14} /> : null}
          >
            {acting ? 'Working…' : 'Mark declined'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'delete'} onClose={closeDialog}>
        <DialogTitle>Delete draft {quote.number}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the draft. You can restore it later from your
            audit log if needed, but it disappears from this list.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? (
                <CircularProgress size={14} />
              ) : (
                <DeleteOutlineRoundedIcon />
              )
            }
          >
            {acting ? 'Deleting…' : 'Delete draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingAction === 'convert'} onClose={closeDialog}>
        <DialogTitle>Convert {quote.number} to an invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            We&apos;ll create a new invoice with the same line items (
            {fmtUsd(quote.total)} total) linked back to this estimate.
            You&apos;ll land on the invoice editor next so you can set the due
            date and review terms before sending.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={acting}>
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleConfirm}
            disabled={acting}
            startIcon={
              acting ? <CircularProgress size={14} /> : <SwapHorizRoundedIcon />
            }
          >
            {acting ? 'Converting…' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || 'info'}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}

function BackLink({ to }) {
  return (
    <Tooltip title="Back to estimates">
      <IconButton
        size="small"
        component={RouterLink}
        to={to}
        sx={{ color: 'text.secondary' }}
        aria-label="Back to estimates"
      >
        <ArrowBackRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function customerName(customer) {
  if (!customer) return 'Customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  const joined = `${fn} ${ln}`.trim();
  return joined || 'Customer';
}
